import React, { useMemo, useState } from 'react'
import { Formik } from 'formik'
import useTrans from '@hooks/useTrans'
import { newsletterSectionValidation } from '@utils/validationSchemas'
import { handleSubscribeNewsletterTracking } from '@utils/tracking'
import { useNewsletterSubscriptionMutation } from '@gql/graphql'
import { Button, FormCheckbox, FormField, FormText, Modal, WContainer } from '@components/atoms'
import { useRouter } from 'next/router'
import styles from './LandingNewsletterRegistrationForm.module.sass'

interface Props {
  className?: string
}

const LandingNewsletterRegistrationForm = (props: Props) => {
  const { className = '' } = props

  const t = useTrans()
  const router = useRouter()

  const [subscribe, { loading }] = useNewsletterSubscriptionMutation()

  const [modalOpen, setModalOpen] = useState(false)
  const [esito, setEsito] = useState({ status: false, message: '' })
  const emailFromQuery = useMemo(() => {
    const queryParam = router.query?.emailuser
    if (typeof queryParam !== 'string') {
      return ''
    }
    return queryParam.trim()
  }, [router.query?.emailuser])
  const initialValues = useMemo(
    () => ({ email: emailFromQuery, privacy: false }),
    [emailFromQuery]
  )

  return (
    <div className={`${styles.root} ${className}`}>
      <WContainer variant="xs">
        <div className={styles.layout}>
          <Formik
            validationSchema={newsletterSectionValidation(t)}
            initialValues={initialValues}
            enableReinitialize
            onSubmit={async ({ email }, { resetForm }) => {
              const {
                data: {
                  newsletterSubscription: { status, message },
                },
              } = await subscribe({ variables: { email } })
              if (status) {
                await handleSubscribeNewsletterTracking(router.asPath)
              }
              setEsito({ status, message })
              setModalOpen(true)
              resetForm({ values: initialValues })
            }}
          >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
              <form className={styles.form} onSubmit={handleSubmit}>
                <FormField
                  id="landing_newsletter_registration_email"
                  layout="full"
                  label={t('Indirizzo email*')}
                  errorMessage={errors.email}
                  status={touched.email ? (errors.email ? 'error' : 'success') : null}
                >
                  <FormText
                    id="landing_newsletter_registration_email"
                    type="email"
                    placeholder={t('Il tuo indirizzo email')}
                    onBlur={handleBlur('email')}
                    value={values.email || ''}
                    onChange={handleChange('email')}
                    status={touched.email ? (errors.email ? 'error' : 'success') : null}
                  />
                </FormField>

                <FormField
                  id="landing_newsletter_registration_privacy"
                  layout="full"
                  errorMessage={errors.privacy}
                  status={touched.privacy ? (errors.privacy ? 'error' : 'success') : null}
                >
                  <FormCheckbox
                    id="landing_newsletter_registration_privacy"
                    label={t(
                      "Avendo letto l'Informativa Privacy, esprimo il consenso al ricevimento della newsletter. Resta inteso che potrò revocare in qualunque momento il presente consenso."
                    )}
                    checked={values.privacy}
                    status={touched.privacy ? (errors.privacy ? 'error' : 'success') : null}
                    onChange={handleChange('privacy')}
                  />
                </FormField>

                <Button
                  type="submit"
                  variant="primary"
                  label={t('Iscriviti alla newsletter')}
                  className={styles.submit}
                  loading={loading}
                />
              </form>
            )}
          </Formik>
        </div>
      </WContainer>
      <Modal
        id="landing-newsletter-registration-modal"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={esito.status ? t('Il tuo sconto sta arrivando!') : t('Iscrizione già attiva')}
        description={esito.message}
        cta={
          <Button
            label={
              esito.status
                ? t('Esplora il mondo {0}', [process.env.BRAND])
                : t('Torna alla homepage')
            }
            href="/"
            onClick={() => setModalOpen(false)}
          />
        }
        className={`${styles.modal} ${className}`}
      />
    </div>
  )
}

export default LandingNewsletterRegistrationForm
